import { ClusterHtmlComponent } from '../shared/components/cluster-html/cluster-html.component';

import { AuthService } from '../auth/auth.service';
import { LanguageService } from '../shared/services/language.service';
import { SmwService, StationInfo } from './smw.service';

import { DatexPipe } from '../shared/datex.pipe';
import { KeyPipe } from '../shared/key.pipe';
import { RoundPipe } from '../shared/round.pipe';
import { GetGmaAlarmColorPipe } from './pipes/get-gma-alarm-color.pipe';
import { GetMarkerColorClassPipe } from './pipes/get-marker-color-class.pipe';
import { HasUserEppPipe } from './pipes/has-user-epp.pipe';
import { IsAvailableParameterPipe } from './pipes/is-available-parameter.pipe';
import { StationIconMapPipe } from './pipes/station-icon-map.pipe';

import { WmIconDirective } from '../shared/wm-icon.directive';

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { environment } from '../../environments/environment';

import { isEmptyObject, isNil } from '../../utils/utils';
import { setPigeonsRoute } from '../constants/constants';
import { MAX_MERCATOR_LAT } from '../map/constants/constants';
import { getMarkerColorClass } from './constants/constants';
import { LngLatBounds, LngLatBoundsLike, Map } from 'mapbox-gl';

export const MODULES = [
  CommonModule,
  MatIconModule,
  MatButtonModule,
  MatTooltipModule,
  MatCardModule,
  RouterModule,
  TranslateModule,
];
export const COMPONENTS = [ClusterHtmlComponent, FaIconComponent];
export const PIPES = [
  DatexPipe,
  RoundPipe,
  KeyPipe,
  IsAvailableParameterPipe,
  HasUserEppPipe,
  GetGmaAlarmColorPipe,
  GetMarkerColorClassPipe,
  StationIconMapPipe,
];
export const DIRECTIVES = [WmIconDirective];

@Component({
  selector: 'app-smw-overview-map',
  templateUrl: './smw-overview-map.component.html',
  styleUrls: ['./smw-overview-map.component.scss', './smw.component.scss'],
  imports: [...MODULES, ...COMPONENTS, ...PIPES, ...DIRECTIVES],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmwOverviewMapComponent implements OnInit {
  private injector = inject(Injector);
  public router = inject(Router);
  public smwService = inject(SmwService);
  public auth = inject(AuthService);
  public languageService = inject(LanguageService);

  clusterHtmlComponent = viewChild<ClusterHtmlComponent>(ClusterHtmlComponent);
  stations = input<Array<StationInfo>>();
  latestSmwData = input<any>();
  rowHeight = input<any>();
  media = input<any>();
  webcamAvailable = input<boolean>();
  webcamImages = input<any>();
  isVisible = input<boolean>(false);
  navigateToStationView = output<any>();

  observer;

  mapboxAccessToken = environment.mapbox.accessToken;

  bounds = signal<LngLatBoundsLike>(undefined);
  showLayerSelect = false;
  data = signal<GeoJSON.FeatureCollection<GeoJSON.Point>>(undefined);

  map: Map;

  parameters = [
    'gmaAlarm',
    'tt',
    'road_tt',
    't5',
    'tg0',
    'tg5',
    'tg5_2',
    'td',
    'road_condition',
  ];
  summerParameters = [
    'rh',
    'vv',
    'n',
    'nlow',
    'hcl',
    'tt',
    'td',
    'humidityIndex',
    'inversionIndex',
    'fx',
    'ff',
    'ww',
  ];

  availableParameters = signal([]);
  clusterTabParameters = signal([]);

  boundGetMarkerColorClassFn = signal<Function>(undefined);
  boundGetGmaAlarmMarkerColorFn = signal<Function>(undefined);

  get mapLayers() {
    return this.smwService.mapLayers;
  }

  activeMapLayerName = computed(() => {
    return this.smwService.getActiveMapLayer()['name'];
  });

  activeMapLayerStyle = computed(() => {
    return this.smwService.getActiveMapLayer()['style'];
  });

  isPigeonCustomer = computed(() => {
    return this.auth.isPigeonCustomer();
  });

  constructor() {
    this.listenToIsVisible();
    this.listenToFormatMapboxData();
  }

  ngOnInit(): void {
    this.setBoundingBox();
    this.checkParameters();
    this.bindClusterFuntions();
    this.formatMapboxData();
  }

  onMapLoad(map: { type: any; target: Map }) {
    this.map = map.target;
    setPigeonsRoute(map.target, this.auth.isProductActive('pigeonProducts'));
  }

  setBoundingBox(): void {
    const coords = [];
    for (const station of this.stations()) {
      const lon = Number(station.longitude);
      const lat = Number(station.latitude);

      if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;

      coords.push([lon, lat]);
    }

    if (coords.length === 0) return null;

    const lons = coords.map(c => c[0] as number);
    const lats = coords.map(c => c[1] as number);

    const west = Math.min(...lons);
    const east = Math.max(...lons);
    const south = Math.min(...lats);
    const north = Math.max(...lats);

    const clampedSouth = this.clampLat(south);
    const clampedNorth = this.clampLat(north);

    this.bounds.set(
      new LngLatBounds([west, clampedSouth], [east, clampedNorth])
    );
  }

  private clampLat(lat: number): number {
    return Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat));
  }

  checkParameters(): void {
    this.availableParameters.set([]);
    this.clusterTabParameters.set([]);
    const parameters = this.isPigeonCustomer()
      ? this.summerParameters
      : this.parameters;
    const gmaAlarm = this.isGmaAlarmAvailable();
    const smwStationsValues = Object.values(this.latestSmwData());
    for (let i = 0; i < parameters.length; i++) {
      if (parameters[i] === 'gmaAlarm' && !gmaAlarm) {
        continue;
      }
      if (
        smwStationsValues.some(smwStationValues => {
          return !isNil(smwStationValues[parameters[i]]);
        })
      ) {
        this.availableParameters.update(availableParameters => {
          return [...availableParameters, parameters[i]];
        });
      }
    }

    this.clusterTabParameters.set([
      ...this.availableParameters(),
      'webcam',
      'dd',
    ]);
  }

  isGmaAlarmAvailable(): boolean {
    for (const station of this.stations()) {
      if (station.gmaAlarm) {
        return true;
      }
    }
    return false;
  }

  getMarkerColorClass(parameter: string, value: number): string {
    return getMarkerColorClass(
      parameter,
      value,
      this.isPigeonCustomer(),
      false //this.smwService.greenTableDesign()
    );
  }

  setActiveMapLayerByName(mapLayerName: string) {
    this.smwService.setActiveMapLayer(mapLayerName);
  }

  getGmaAlarmColor(stationDataByAlarmParameter: any) {
    return this.smwService.getGmaAlarmColor(stationDataByAlarmParameter);
  }

  private bindClusterFuntions() {
    this.boundGetMarkerColorClassFn.set(this.getMarkerColorClass.bind(this));
    this.boundGetGmaAlarmMarkerColorFn.set(this.getGmaAlarmColor.bind(this));
  }

  private formatMapboxData() {
    const latestSmwData = this.latestSmwData();
    this.data.set({
      type: 'FeatureCollection',
      features: this.stations()
        .filter(
          station =>
            !isNil(latestSmwData[station.stationId]) &&
            !isEmptyObject(latestSmwData[station.stationId])
        )
        .map(station => {
          let featuresProperties = [...this.clusterTabParameters()].reduce(
            (acc, parameter) => {
              if (!isNil(latestSmwData[station.stationId][parameter])) {
                // Weather condition with value 0, 508, 509 and 510 are not displayed
                if (
                  parameter == 'ww' &&
                  (latestSmwData[station.stationId][parameter] === 0 ||
                    latestSmwData[station.stationId][parameter] === 508 ||
                    latestSmwData[station.stationId][parameter] === 509 ||
                    latestSmwData[station.stationId][parameter] === 510)
                ) {
                  return { ...acc };
                } else {
                  return {
                    ...acc,
                    [parameter]: latestSmwData[station.stationId][parameter],
                  };
                }
              } else {
                return {
                  ...acc,
                };
              }
            },
            {}
          );
          featuresProperties = {
            ...featuresProperties,
            stationId: station.stationId,
            type: station.type,
            name: station.name,
            webcam: this.webcamImages()[station.stationId],
          };
          return {
            id: station.stationId,
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [station.longitude, station.latitude],
            },
            properties: featuresProperties,
          };
        }),
    });
  }

  onWebcamClick() {
    if (this.auth.isZuerichCustomer()) {
      window.open(
        'https://s3.eu-west-1.amazonaws.com/obs.gma.lufft.wm/waiblingen.html',
        '_blank'
      );
    }
  }

  private listenToFormatMapboxData() {
    effect(
      () => {
        this.latestSmwData();
        this.webcamImages();
        this.stations();
        untracked(() => {
          this.formatMapboxData();
        });
      },
      { injector: this.injector }
    );
  }

  private listenToIsVisible() {
    effect(
      () => {
        const isVisible = this.isVisible();
        if (isVisible && this.map) {
          setTimeout(() => {
            this.map.resize();
            this.map.once('idle', () => {
              this.map.jumpTo({
                center: this.map.getCenter(),
                zoom: this.map.getZoom(),
              });
            });
          }, 100);
        }
      },
      { injector: this.injector }
    );
  }
}
